/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/microsoft-outlook" */ '@atlassian/logo-third-party/microsoft-outlook'
		).then((m) => m.MicrosoftOutlookIcon),
	{ moduleId: '@atlassian/logo-third-party/microsoft-outlook' },
);

const LazyOutlookCalendarIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-outlook-calendar" */ './utils').then(
			(m) => m.OutlookCalendarIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/outlook-calendar/icon' },
);

const LazyOutlookCalendarSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-outlook-calendar-search-result" */ './utils').then(
			(m) => m.OutlookCalendarSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/outlook-calendar/search-result-icon' },
);

const LazyOutlookCalendarIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-outlook-calendar-borderless" */ './utils').then(
			(m) => m.OutlookCalendarIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/outlook-calendar/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const OutlookCalendarIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOutlookCalendarIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const OutlookCalendarSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOutlookCalendarSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const OutlookCalendarIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOutlookCalendarIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
